import type { Channel } from '../../../shared/alerts/rule';
import type { AuthClient } from '../../../shared/auth/flow';
import type { Moment } from '../../../shared/alerts/signup-rule';
import {
  countryOptions,
  guessedNote,
  type CountrySource,
} from '../../../shared/feed/countries';
import { countryOfZone } from '../currency-guess';
import { DEALS, type Region, type SampleDeal } from '../data';
import {
  DEFAULT_ANSWERS,
  STEP,
  STEP_COUNT,
  afterSignup,
  isCountry,
  oauthReturn,
  parseStored,
  preview,
  providerHref,
  sentence,
  signupRule,
  type AlertRow,
  type Answers,
  type Preview,
  type Stored,
  type Ticket,
} from '../onboarding';
import { pageClerk, type PageClerkOptions } from './clerk';
import { now as clockNow, onTick } from './clock';
import { $, $$, attr, tablist, text } from './dom';
import { countryCombobox } from './country-combobox';
import { countryGuess } from './geo';
import { signupPanel } from './signup';
import { timeZone } from './widgets';

/* -------------------------------------------------------------------------
 * /get-started: the rule builder. Four steps build a rule, the sentence
 * reads it back, and the preview replays the last 30 days of sample raises
 * against it on the page's clock. Step 4 signs the visitor up, or in, on
 * the page (signup.ts), then goes to the dashboard, which saves the rule.
 * The answers persist in this browser, so a refresh or a trip to /platforms
 * keeps them.
 * ---------------------------------------------------------------------- */

const KEY = 'rr.onboarding';

function load(): Stored | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? parseStored(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function store({ answers, countryChosen }: Stored): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...answers, countryChosen }));
  } catch {
    /* the answers still hold for this visit */
  }
}

function fresh(): Stored {
  return { answers: structuredClone(DEFAULT_ANSWERS), countryChosen: false };
}

/**
 * /platforms' Details dialog, on its way here: the platform's market joins
 * the regions chosen. None chosen means every region, which has it already.
 */
export function addOnboardingRegion(region: Region): void {
  const kept = load() ?? fresh();
  const { regions } = kept.answers;
  if (regions.length > 0 && !regions.includes(region)) {
    kept.answers.regions = [...regions, region];
  }
  store(kept);
}

/** A guess at where the visitor lives, and what it went by. */
export type Guess = (
  country: string,
  source: Exclude<CountrySource, 'saved'>,
) => void;

/**
 * Where the visitor most likely lives, as the dashboard guesses it: at once
 * from the browser's time zone where the page knows it, then from the
 * server, which goes by their address and else by the zone, over every zone
 * (GET /api/geo/country). The server has the last word.
 */
function locate(place: Guess): void {
  const zone = timeZone();
  const first = countryOfZone(zone);
  if (isCountry(first)) place(first, 'timezone');
  void countryGuess(zone).then((guess) => {
    if (guess && isCountry(guess.country)) place(guess.country, guess.source);
  });
}

/** Every country the dashboard's picker lists, as the server sent them. */
function readCountries(): string[] {
  try {
    const raw = document.getElementById('ob-countries')?.textContent ?? '[]';
    const codes = JSON.parse(raw) as unknown;
    return Array.isArray(codes) ? codes.filter(isCountry) : [];
  } catch {
    return [];
  }
}

export interface OnboardingDeps {
  now: () => number;
  deals: SampleDeal[];
  /** Leaves for the dashboard, or for the sign-in page. */
  go: (href: string) => void;
  locate: (place: Guess) => void;
  /** Clerk, as step 4's sign-up asks for it (client/clerk.ts). */
  auth: (options: PageClerkOptions) => AuthClient;
}

const DEFAULT_DEPS: OnboardingDeps = {
  now: clockNow,
  deals: DEALS,
  go: (href) => window.location.assign(href),
  locate,
  auth: pageClerk,
};

/** One alert in the list, as the server draws it (views/get-started.hbs). */
function buildRow(): HTMLButtonElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = 'onb-row';
  const glyph = document.createElement('span');
  glyph.className = 'onb-glyph';
  attr(glyph, 'aria-hidden', 'true');
  const title = document.createElement('span');
  title.className = 'onb-row-t';
  const kind = document.createElement('span');
  title.append(document.createTextNode(''), kind);
  const when = document.createElement('span');
  when.className = 'onb-row-w num';
  const sub = document.createElement('span');
  sub.className = 'onb-row-s';
  el.append(glyph, title, when, sub);
  return el;
}

function paintRow(el: HTMLElement, r: AlertRow): void {
  const [glyph, title, when, sub] = Array.from(el.children) as HTMLElement[];
  el.classList.toggle('is-out', !r.eligible);
  attr(el, 'aria-pressed', String(r.selected));
  el.dataset.key = r.key;
  glyph.className = `onb-glyph onb-b-${r.tone}`;
  text(glyph, r.glyph);
  const name = title.firstChild;
  const deal = `${r.deal} `;
  if (name && name.nodeValue !== deal) name.nodeValue = deal;
  const kind = title.lastElementChild as HTMLElement;
  kind.className = `onb-row-k onb-t-${r.tone}`;
  text(kind, `· ${r.kind}`);
  text(when, r.when);
  text(sub, r.sub);
}

export function onboardingPage(
  root: HTMLElement,
  deps: OnboardingDeps = DEFAULT_DEPS,
): void {
  const kept = load() ?? fresh();
  const answers: Answers = kept.answers;
  let countryChosen = kept.countryChosen;
  let step = 0;
  // The alert the visitor picked from the list; null is the newest.
  let selected: string | null = null;

  const sentenceEl = $(root, '[data-sentence]');
  const panels = $$(root, '[data-step-panel]');
  const tabs = $$<HTMLButtonElement>(root, '[role="tab"]');
  const back = $<HTMLButtonElement>(root, '[data-back]');
  const next = $<HTMLButtonElement>(root, '[data-next]');
  const prompt = $(root, '[data-prompt]');
  const picker = $(root, '[data-country]');
  const guessNote = $(root, '[data-country-note]');
  const onlyMine = $<HTMLButtonElement>(root, '[data-only-eligible]');
  const rowsEl = $(root, '[data-rows]');
  if (!sentenceEl || !back || !next || !picker || !onlyMine || !rowsEl) return;

  const save = () => store({ answers, countryChosen });

  // ---- The sentence -------------------------------------------------------------

  const paintSentence = () => {
    const nodes = sentence(answers).map((part) => {
      if (part.step === null) return document.createTextNode(part.text);
      // A span, not a button, so a long token wraps with the line.
      const token = document.createElement('span');
      token.className = 'onb-tok';
      attr(token, 'role', 'button');
      token.tabIndex = 0;
      token.dataset.step = String(part.step);
      token.classList.toggle('is-current', part.step === step);
      token.textContent = part.text;
      return token;
    });
    sentenceEl.replaceChildren(...nodes);
  };
  const tokenOf = (e: Event) =>
    (e.target as Element).closest<HTMLElement>('.onb-tok');
  sentenceEl.addEventListener('click', (e) => {
    const token = tokenOf(e);
    if (token) show(Number(token.dataset.step), true);
  });
  sentenceEl.addEventListener('keydown', (e) => {
    const token = tokenOf(e);
    if (!token || (e.key !== 'Enter' && e.key !== ' ')) return;
    e.preventDefault();
    show(Number(token.dataset.step), true);
  });

  // ---- The steps ----------------------------------------------------------------

  const selectTab = tablist($(root, '[data-tabs]')!, (index) =>
    show(index, false),
  );

  const show = (index: number, focus: boolean) => {
    step = index;
    selectTab(index);
    tabs.forEach((t, i) => t.classList.toggle('is-done', i <= index));
    panels.forEach((p, i) => (p.hidden = i !== index));
    back.classList.toggle('is-gone', index === 0);
    next.hidden = index === STEP.signUp;
    text(next, index === STEP.signUp - 1 ? 'Create account' : 'Continue');
    if (prompt) prompt.hidden = index === STEP.signUp;
    $$(sentenceEl, '.onb-tok').forEach((t) =>
      t.classList.toggle('is-current', Number(t.dataset.step) === index),
    );
    if (focus) $(panels[index], '.onb-h2')?.focus();
  };

  next.addEventListener('click', () =>
    show(Math.min(step + 1, STEP_COUNT - 1), true),
  );
  back.addEventListener('click', () => show(Math.max(0, step - 1), true));
  $(root, '[data-to-signup]')?.addEventListener('click', () =>
    show(STEP.signUp, true),
  );

  // ---- The choices --------------------------------------------------------------

  // "Continue with Google" or Apple. Each link's own address is the
  // sign-in page, which starts the provider itself, the rule riding along:
  // kept current for a new tab and the page without its script. A click
  // starts it here (signupPanel).
  const providerLinks = $$<HTMLAnchorElement>(root, 'a[data-provider]');
  const paintProviders = () => {
    const rule = signupRule(answers, countryChosen);
    for (const link of providerLinks) {
      const provider = link.dataset.provider;
      if (provider === 'google' || provider === 'apple') {
        attr(link, 'href', providerHref(provider, rule));
      }
    }
  };

  /** Everything that reads the rule, after it changed. */
  const changed = () => {
    selected = null;
    paintSentence();
    paintPreview(deps.now());
    paintProviders();
    save();
  };

  const chipGroups = $$(root, '[data-field]').map((group) => {
    const field = group.dataset.field as
      'regions' | 'sectors' | 'ticket' | 'moments' | 'channels';
    const chips = $$<HTMLButtonElement>(group, 'button[data-value]');
    const isOn = (v: string) =>
      field === 'ticket'
        ? String(answers.ticket) === v
        : (answers[field] as string[]).includes(v);
    const paint = () => {
      for (const chip of chips) {
        attr(chip, 'aria-pressed', String(isOn(chip.dataset.value ?? '')));
      }
    };
    for (const chip of chips) {
      chip.addEventListener('click', () => {
        const v = chip.dataset.value ?? '';
        if (field === 'ticket') {
          answers.ticket = Number(v) as Ticket;
        } else {
          const list = answers[field] as string[];
          const toggled = list.includes(v)
            ? list.filter((x) => x !== v)
            : [...list, v];
          if (field === 'regions') answers.regions = toggled as Region[];
          else if (field === 'moments') answers.moments = toggled as Moment[];
          else if (field === 'channels')
            answers.channels = toggled as Channel[];
          else answers.sectors = toggled;
        }
        paint();
        changed();
      });
    }
    return paint;
  });

  // "You live in", as the dashboard's picker: what was picked, else the
  // guess, which stands first in the list and is never saved.
  let guess = answers.country;
  let guessSource: Exclude<CountrySource, 'saved'> | null = null;
  const countries = countryCombobox(
    picker,
    countryOptions(readCountries()),
    { value: countryChosen ? answers.country : '', guess },
    (value) => {
      countryChosen = value !== '';
      answers.country = value || guess;
      paintCountry();
      changed();
    },
  );
  const paintCountry = () => {
    countries?.set(countryChosen ? answers.country : '', guess);
    if (guessNote) {
      const note = !countryChosen && guessSource;
      guessNote.hidden = !note;
      text(guessNote, note ? guessedNote(guessSource!) : '');
    }
    attr(onlyMine, 'aria-pressed', String(answers.onlyEligible));
  };
  onlyMine.addEventListener('click', () => {
    answers.onlyEligible = !answers.onlyEligible;
    paintCountry();
    changed();
  });

  // ---- The preview --------------------------------------------------------------

  const featured = $(root, '[data-featured]');
  const list = $(root, '[data-list]');
  const empty = $(root, '[data-empty]');
  const more = $(root, '[data-more]');
  const nextAlert = $(root, '[data-next-alert]');
  const timer = $(root, '[data-f-cd]');

  // Rows by alert, so a row that stays keeps its element and does not play
  // its entrance again. The server's rows are the first ones.
  const mounted = new Map<string, HTMLElement>();
  for (const el of $$(rowsEl, '.onb-row')) {
    if (el.dataset.key) mounted.set(el.dataset.key, el);
  }

  const paintRows = (rows: AlertRow[]) => {
    const keep = new Set(rows.map((r) => r.key));
    for (const [key, el] of mounted) {
      if (!keep.has(key)) {
        el.remove();
        mounted.delete(key);
      }
    }
    const els = rows.map((r, i) => {
      let el = mounted.get(r.key);
      if (!el) {
        el = buildRow();
        el.classList.add('is-new');
        el.style.setProperty('--i', String(i));
        mounted.set(r.key, el);
      }
      paintRow(el, r);
      return el;
    });
    const inOrder =
      rowsEl.children.length === els.length &&
      els.every((el, i) => rowsEl.children[i] === el);
    if (!inOrder) rowsEl.replaceChildren(...els);
  };
  // An entrance plays once: a row moved later keeps still.
  rowsEl.addEventListener('animationend', (e) => {
    (e.target as Element).classList.remove('is-new');
  });
  rowsEl.addEventListener('click', (e) => {
    const row = (e.target as Element).closest<HTMLElement>('.onb-row');
    if (!row?.dataset.key) return;
    selected = row.dataset.key;
    paintPreview(deps.now());
  });

  const paintFeatured = (p: Preview) => {
    if (!featured) return;
    featured.hidden = !p.featured;
    const f = p.featured;
    if (!f) return;
    text($(featured, '[data-f-sender]'), f.sender);
    text($(featured, '[data-f-sent]'), f.sent);
    const kicker = $(featured, '[data-f-kicker]');
    if (kicker) kicker.className = `onb-kicker onb-t-${f.tone}`;
    text(kicker, f.kicker);
    text($(featured, '[data-f-title]'), f.title);
    text($(featured, '[data-f-line]'), f.line);
    const urgency = $(featured, '[data-f-urgency]');
    if (urgency) urgency.className = `onb-closes tone-${f.urgency}`;
    text($(featured, '[data-f-closes]'), f.closes);
    text(timer, f.countdown);
    attr(timer, 'aria-label', f.spoken);
    const elig = $(featured, '[data-f-elig]');
    elig?.classList.toggle('is-ok', f.eligible);
    elig?.classList.toggle('is-out', !f.eligible);
    text(elig, f.eligibility);
    text($(featured, '[data-f-prov]'), f.provenance);
  };

  // The clock moves it too: an alert falls due, a countdown ticks down.
  const paintPreview = (n: number) => {
    const p = preview(answers, deps.deals, n, selected);
    text($(root, '[data-clock]'), p.clock);
    text($(root, '[data-count]'), p.count);
    text($(root, '[data-count-label]'), p.label);
    paintFeatured(p);
    if (list) list.hidden = p.empty;
    paintRows(p.rows);
    if (more) {
      more.hidden = p.more === null;
      text(more, p.more ?? '');
    }
    if (empty) empty.hidden = !p.empty;
    if (nextAlert) {
      nextAlert.hidden = p.next === null;
      text($(nextAlert, '[data-next-label]'), p.next?.label ?? '');
      text($(nextAlert, '[data-next-text]'), p.next?.text ?? '');
    }
  };

  // ---- Sign up ------------------------------------------------------------------

  // Up, or in, on this page, then to the dashboard, which saves the rule as
  // it then stands (afterSignup, web/src/handoff.ts).
  const ruleNow = () => signupRule(answers, countryChosen);
  signupPanel(root, {
    client: deps.auth({
      publishableKey: $(root, '[data-providers]')?.dataset.clerkKey ?? null,
      oauthReturn: (provider) =>
        oauthReturn(provider, ruleNow(), window.location.origin),
      signedIn: () => deps.go(afterSignup(ruleNow())),
    }),
    destination: () => {
      save();
      return afterSignup(ruleNow());
    },
    go: deps.go,
  });

  // ---- Start --------------------------------------------------------------------

  chipGroups.forEach((paint) => paint());
  paintCountry();
  paintSentence();
  paintProviders();
  show(0, false);
  paintPreview(deps.now());
  onTick(() => paintPreview(deps.now()));

  deps.locate((country, source) => {
    guess = country;
    guessSource = source;
    const moved = !countryChosen && answers.country !== country;
    if (moved) answers.country = country;
    paintCountry();
    if (moved) changed();
  });
}
